import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, Button } from "@narmi/design_system";
import { Dialog } from "cerulean";
import { entities } from "byzantine";
import {
  haveImportedPaymentsChanged,
  importResponseToBulkPayments,
} from "byzantine/src/dbbl/businessLogic/entities/achImport";
import {
  getUpdatedBulkPaymentSchema,
  paymentsSchema,
  calculateBulkTotals,
  filterExcludedRowErrors,
  hasValidationErrors,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import {
  getReadableTimeFromCutoff,
  getStartDate,
  LimitsForDisplayType,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { useSudoContext } from "../../SudoContext";
import HeaderWithSteps from "../../HeaderWithSteps";
import Container from "../../Container";
import LimitsAndDisclosuresDialog from "../../transfer/ach/LimitsAndDisclosuresDialog";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { useACHImportContext } from "../ACHImportContext";
import { getAchImportPermittedActions } from "./achImportDefaults";
import styles from "./CreateBulk.module.scss";
import bulkTableStyles from "./table/BulkPaymentTable.module.scss";
import BulkTotalsDisplay from "./BulkTotalsDisplay";
import ExpeditePaymentsCard from "./ExpeditePaymentsCard";
import { TableErrorSummary, BulkPaymentTable } from "./table";
import { useTemplateContext } from "./templates/TemplateContext";

const PaymentsTableScreen = ({ limits }: { limits: LimitsForDisplayType }) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { establishSudo } = useSudoContext();
  const { setCloseRouteOverride } = useSudoContext();
  const {
    sudo_mode_required_for_bulk_payments: sudoModeRequired,
    ach_next_cutoff: achNextCutoff,
  } = useInstitutionSettings();
  const features = useUserFeatures();
  const {
    expedited_payments: expeditedPaymentsFeature,
    fednow_send: fedNowFeature,
    same_day_ach: sameDayFeature,
    ach_templates: achTemplatesFeature,
  } = features;
  const { isTemplateMode, allowExpedite, excludedRows, templateQueryString } =
    useTemplateContext();
  const { importType, nachaResponse } = useACHImportContext();
  const isImportMode = importType !== null;
  const permittedTransactionTypes = isImportMode
    ? getAchImportPermittedActions(features)
    : undefined;
  const [showLimitsDialog, setShowLimitsDialog] = useState(false);
  const [showDiscardEditsDialog, setShowDiscardEditsDialog] = useState(false);
  const [clickedReview, setClickedReview] = useState(false);
  const [validationSchemaReady, setValidationSchemaReady] = useState(false);
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const { setValidationSchema, setErrors, validateForm, setFormikState } = form;
  const { payments } = form.values;

  // for template mode, only count included payments in totals
  const includedPayments = useMemo(
    () =>
      isTemplateMode
        ? payments.filter((_, i) => !excludedRows.has(i))
        : payments,
    [payments, isTemplateMode, excludedRows]
  );

  const {
    collectTotalFormatted,
    sendTotalFormatted,
    netTotalFormattedWithSign,
  } = calculateBulkTotals(includedPayments);

  const readableCutoffTime = getReadableTimeFromCutoff(achNextCutoff);

  const expeditableCount = useMemo(
    () =>
      includedPayments.filter(
        (p) => p.speedStatus === "instant" || p.speedStatus === "sameDay"
      ).length,
    [includedPayments]
  );

  // in template mode, only show expedite if template allows it
  const showExpediteCard =
    expeditableCount > 0 && (!isTemplateMode || allowExpedite);

  const allPaymentsExcluded =
    isTemplateMode &&
    payments.length > 0 &&
    excludedRows.size >= payments.length;

  // reset chosen expedite option if no payments are expeditable
  useEffect(() => {
    if (
      expeditedPaymentsFeature &&
      expeditableCount === 0 &&
      form.values.chosenExpediteOption
    ) {
      form.setFieldValue("chosenExpediteOption", "");
    }
  }, [expeditableCount, expeditedPaymentsFeature, form]);

  useEffect(() => {
    const schemaTimer = window.setTimeout(() => {
      // if user has expedited payments feature enabled, and can send instant payments
      // then we will set the new schema that allows/validates user to choose date before cutoff
      if (expeditedPaymentsFeature && fedNowFeature) {
        const startDate = getStartDate(achNextCutoff);
        const schema = getUpdatedBulkPaymentSchema(
          startDate,
          Boolean(fedNowFeature && !sameDayFeature),
          permittedTransactionTypes
        );
        setValidationSchema(schema);
      } else {
        // when user lands on this screen, set validation schema to include "payments"
        setValidationSchema((dbblL10n) =>
          form.validationSchema.concat(
            paymentsSchema(dbblL10n, permittedTransactionTypes)
          )
        );
      }
      setValidationSchemaReady(true);
    }, 0);

    return () => window.clearTimeout(schemaTimer);
  }, []);

  // Import mode: validate once on landing so the summary banner and per-row
  // error cells reflect the already-known row errors without requiring a
  // "Review" click. The error getters (and cell highlighting) are gated on
  // submitCount > 0, so populating errors is not enough — we also bump
  // submitCount via setFormikState.
  const hasValidatedOnLanding = useRef(false);
  useEffect(() => {
    if (!isImportMode || !validationSchemaReady || hasValidatedOnLanding.current) {
      return;
    }
    hasValidatedOnLanding.current = true;

    validateForm(form.values).then((errors) => {
        const filteredErrors =
          isTemplateMode && excludedRows.size > 0
            ? filterExcludedRowErrors(errors, excludedRows)
            : errors;

        setErrors(filteredErrors);
        // bump submitCount so the submitCount > 0 gate in TableErrorSummary /
        // BulkPaymentRow / BulkPaymentTable surfaces the populated errors
        setFormikState((prev) => ({
          ...prev,
          submitCount: Math.max(prev.submitCount, 1),
        }));
      });
  }, [form.values, isImportMode, isTemplateMode, excludedRows, setErrors, setFormikState, validateForm, validationSchemaReady]);

  const handleClickReview = async () => {
    setClickedReview(true);

    if (allPaymentsExcluded) {
      return;
    }

    const errors = await validateForm();
    const filteredErrors =
      isTemplateMode && excludedRows.size > 0
        ? filterExcludedRowErrors(errors, excludedRows)
        : errors;

    setErrors(filteredErrors);

    if (hasValidationErrors(filteredErrors)) {
      return;
    }

    if (sudoModeRequired) {
      setCloseRouteOverride(
        `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/payments${templateQueryString}`
      );
      establishSudo(
        `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/review${templateQueryString}`
      );
    } else {
      navigate(`../review${templateQueryString}`);
    }
  };

  const navigateToImportDetails = () => {
    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/upload/additional-details`);
  };

  const toggleLimitsDialog = () => {
    setShowLimitsDialog(!showLimitsDialog);
  };

  return (
    <div
      id="create-bulk-payments-table-container"
      className={styles.paymentsScreenContainer}
    >
      <Container maxWidth="1340px">
        <HeaderWithSteps
          useDisplayFont
          headerText={l10n.getString("bulk-payment-header")}
        />

        <TableErrorSummary
          clickedReview={clickedReview}
          form={form}
          showDateErrors
          allPaymentsExcluded={allPaymentsExcluded}
          isImportMode={isImportMode}
        />
        <div className="margin--top--xs">
          <BulkPaymentTable isImportMode={isImportMode} />
        </div>

        {showExpediteCard && (
          <ExpeditePaymentsCard expeditableCount={expeditableCount} />
        )}
        <BulkTotalsDisplay
          collectTotalFormatted={collectTotalFormatted}
          sendTotalFormatted={sendTotalFormatted}
          netTotalFormattedWithSign={netTotalFormattedWithSign}
        />

        <div className={bulkTableStyles.headingContainer}>
          {readableCutoffTime ? (
            <span>
              {l10n.getString("cutoff-time-copy")}
              <span className="fontWeight--bold">{` ${readableCutoffTime}`}</span>
              .
            </span>
          ) : null}
          <div className={bulkTableStyles.endRowContainer}>
            <Button
              label={l10n.getString("ach-payment-view-limits-bulk-cta")}
              testId="view-limits-cta"
              kind="plain"
              size="s"
              onClick={toggleLimitsDialog}
            />
          </div>
        </div>

        <div className="margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                label={l10n.getString("button-back")}
                testId="cancel-bulk-payment"
                onClick={() => {
                  if (isImportMode) {
                    if (
                      nachaResponse &&
                      haveImportedPaymentsChanged(
                        payments,
                        importResponseToBulkPayments(nachaResponse, importType)
                      )
                    ) {
                      setShowDiscardEditsDialog(true);
                    } else {
                      navigateToImportDetails();
                    }
                  } else {
                    navigate(
                      achTemplatesFeature
                        ? `../details${templateQueryString}`
                        : `..${templateQueryString}`
                    );
                  }
                }}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                label={l10n.getString("heading-review")}
                testId="review-bulk-payment"
                onClick={handleClickReview}
              />
            </Row.Item>
          </Row>
        </div>

        <Dialog
          isOpen={showDiscardEditsDialog}
          title={l10n.getString("ach-import-discard-edits-title")}
          onUserDismiss={() => setShowDiscardEditsDialog(false)}
          footer={
            <Row alignItems="center" justifyContent="end">
              <Row.Item shrink>
                <Button
                  kind="negative"
                  label={l10n.getString("button-cancel")}
                  onClick={() => setShowDiscardEditsDialog(false)}
                />
              </Row.Item>
              <Row.Item shrink>
                <Button
                  label={l10n.getString("button-continue")}
                  testId="confirm-discard-import-edits"
                  onClick={() => {
                    if (nachaResponse) {
                      form.setFieldValue(
                        "payments",
                        importResponseToBulkPayments(nachaResponse, importType)
                      );
                    }
                    setShowDiscardEditsDialog(false);
                    navigateToImportDetails();
                  }}
                />
              </Row.Item>
            </Row>
          }
        >
          {l10n.getString("ach-import-discard-edits-body")}
        </Dialog>

        <LimitsAndDisclosuresDialog
          isOpen={showLimitsDialog}
          handleClose={toggleLimitsDialog}
          limits={limits}
          fromFlow="bulkPayment"
        />
      </Container>
    </div>
  );
};

export default PaymentsTableScreen;
